import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPWA(): React.JSX.Element | null {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);

    useEffect(() => {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (dismissed === 'true') return;

        const handleBeforeInstallPrompt = (e: Event): void => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            if (window.matchMedia('(display-mode: standalone)').matches) return;
            setShowInstallPrompt(true);
        };

        const handleAppInstalled = (): void => {
            setShowInstallPrompt(false);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async (): Promise<void> => {
        if (!deferredPrompt) return;
        await deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
    };

    const handleDismiss = (): void => {
        setShowInstallPrompt(false);
        localStorage.setItem('pwa-install-dismissed', 'true');
    };

    if (!showInstallPrompt) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-fade-in">
            <Card className="shadow-lg border-2 border-primary/20">
                <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg gradient-primary flex-shrink-0">
                            <Download className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground mb-1">Install Hadir</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Install our app for quick access and offline functionality
                            </p>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => void handleInstallClick()} className="flex-1" data-testid="pwa-install-btn">
                                    <Download className="h-3 w-3 mr-1" />
                                    Install
                                </Button>
                                <Button size="sm" variant="ghost" onClick={handleDismiss} data-testid="pwa-dismiss-btn">
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
